import {
  chatsForCriterion,
  DEV_CHATS,
  formatDay,
  HOLD_TITLES,
  type HoldKind,
  holdOf,
  MILESTONE_STATE_TITLES,
  type MilestoneState,
  MODULES,
  milestoneState,
  openOwnerSteps,
  PILOT_MILESTONES,
  pilotCriteria,
  RELEASES,
  REQUIREMENT_STATUS_TITLES,
  REQUIREMENTS,
  type Requirement,
  type RequirementPlace,
  type RequirementStatus,
  STAGE_STATE_TITLES,
  STAGES,
  stageState,
  TZ_EDITION,
  todayKz,
  whenOf,
} from '@epc/contracts';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { dateTime, plural } from '../lib/format';
import { Badge, type Tone } from '../ui/feedback';
import { Checkbox, Input, Select } from '../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../ui/layout';
import { useDevBoardMe } from './devboard/access';

const STATUS_TONE: Record<RequirementStatus, Tone> = {
  planned: 'neutral',
  in_progress: 'info',
  partial: 'warning',
  done: 'success',
};

const HOLD_TONE: Record<HoldKind, Tone> = {
  stage: 'neutral',
  queue: 'neutral',
  dependency: 'info',
  decision: 'accent',
  external: 'accent',
  rework: 'warning',
  acceptance: 'info',
};

/** Разделы ТЗ без своего пункта меню */
const EXTRA_TITLES: Record<string, string> = {
  platform: 'Платформа и приложения',
  tasks: 'Задачи и совещания',
  integrations: 'Интеграции',
  nfr: 'Скорость, безопасность, качество',
  acceptance: 'Приёмка пилота',
  help: 'Помощь, база знаний, поддержка',
  licensing: 'Лицензии, демо и подключение компаний',
  video: 'Видеонаблюдение и видеоаналитика',
  fleet: 'Техника, топливо и выработка',
};
const moduleTitle = (code: string) =>
  MODULES.find((m) => m.code === code)?.title ?? EXTRA_TITLES[code] ?? code;

/** Редакция ТЗ, по которой ведётся работа */
const TZ_REVISION = `${TZ_EDITION.title} от ${formatDay(TZ_EDITION.date)} и ускоренный календарь владельца от ${formatDay(TZ_EDITION.calendar)}; ${TZ_EDITION.texts}`;

const MILESTONE_TONE: Record<MilestoneState, Tone> = { past: 'neutral', now: 'accent', ahead: 'info' };

/** «4.11» или «15–19.12» */
function span(from: string, to?: string): string {
  const short = (iso: string) => formatDay(iso).slice(0, 5);
  if (!to) return short(from);
  return from.slice(0, 7) === to.slice(0, 7)
    ? `${short(from).slice(0, 2)}–${short(to)}`
    : `${short(from)}–${short(to)}`;
}

const isDone = (r: Requirement) => r.status === 'done';
const isPartial = (r: Requirement) => r.status === 'partial' || r.status === 'in_progress';
const waitsCustomer = (r: Requirement) => {
  const h = holdOf(r);
  return h !== null && (h.kind === 'decision' || h.kind === 'external');
};

function PlaceLink({ place }: { place: RequirementPlace }) {
  if (!place.path) return <span className="text-ink">{place.label}</span>;
  const cls = 'text-ink underline decoration-line-strong underline-offset-4 hover:text-accent-text';
  return place.path.startsWith('/api') ? (
    <a href={place.path} target="_blank" rel="noreferrer" className={cls}>
      {place.label}
    </a>
  ) : (
    <Link to={place.path} className={cls}>
      {place.label}
    </Link>
  );
}

/** Где требование работает; для несделанного — раздел, где оно появится */
function Where({ r }: { r: Requirement }) {
  if (r.where?.length) {
    return (
      <ul className="flex flex-col gap-1">
        {r.where.map((p) => (
          <li key={p.label}>
            <PlaceLink place={p} />
          </li>
        ))}
      </ul>
    );
  }
  const mod = MODULES.find((m) => m.code === r.module);
  return (
    <span className="text-ink-2">
      Появится в разделе{' '}
      {mod ? (
        <Link
          to={mod.path}
          className="underline decoration-line-strong underline-offset-4 hover:text-accent-text"
        >
          «{mod.title}»
        </Link>
      ) : (
        `«${moduleTitle(r.module)}»`
      )}
    </span>
  );
}

function Why({ r }: { r: Requirement }) {
  const h = holdOf(r);
  if (!h) return <span className="hidden text-ink-3 md:inline">—</span>;
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge tone={HOLD_TONE[h.kind]}>{HOLD_TITLES[h.kind]}</Badge>
      <span className="text-[13px] leading-[18px] text-ink-2">{h.text}</span>
    </div>
  );
}

/**
 * Доска задач разработки (чат board, 03.11.2026) — отдельным блоком под заголовком и только тем, кому доска открыта
 * (владелец портала и люди, которых он добавил)
 */
function DevBoardLink() {
  const board = useDevBoardMe();
  if (!board) return null;
  return (
    <div className="mb-4 flex md:justify-end">
      <Link
        to="/devboard"
        className="inline-flex h-11 items-center rounded-md border border-line-strong px-4 text-[14px] whitespace-nowrap text-ink hover:bg-tile/70 md:h-10"
      >
        Доска задач разработки
      </Link>
    </div>
  );
}

export function RoadmapPage() {
  const [module, setModule] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('');
  const [hold, setHold] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);
  const [stage, setStage] = useState<number | null>(null);
  const [q, setQ] = useState('');

  const byStage = useMemo(
    () =>
      STAGES.map((s) => {
        const reqs = REQUIREMENTS.filter((r) => r.stage === s.number);
        return {
          ...s,
          state: stageState(s),
          total: reqs.length,
          done: reqs.filter(isDone).length,
          partial: reqs.filter(isPartial).length,
        };
      }),
    [],
  );

  const modules = useMemo(() => {
    const codes = [...new Set(REQUIREMENTS.map((r) => r.module))];
    return codes.map((code) => {
      const reqs = REQUIREMENTS.filter((r) => r.module === code);
      return {
        code,
        title: moduleTitle(code),
        total: reqs.length,
        done: reqs.filter(isDone).length,
        partial: reqs.filter(isPartial).length,
        first: reqs.filter((r) => r.priority === 1).length,
      };
    });
  }, []);

  const holds = useMemo(
    () =>
      (Object.keys(HOLD_TITLES) as HoldKind[]).map((kind) => ({
        kind,
        title: HOLD_TITLES[kind],
        count: REQUIREMENTS.filter((r) => holdOf(r)?.kind === kind).length,
      })),
    [],
  );

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return REQUIREMENTS.filter((r) => {
      const h = holdOf(r);
      return (
        (!module || r.module === module) &&
        (!priority || String(r.priority) === priority) &&
        (!status ||
          (status === 'not_done'
            ? r.status !== 'done'
            : status === 'done_any'
              ? r.status !== 'planned'
              : r.status === status)) &&
        (!hold || h?.kind === hold) &&
        (!onlyNew || Boolean(r.added)) &&
        (stage === null || r.stage === stage) &&
        (!s ||
          r.code.toLowerCase().includes(s) ||
          r.text.toLowerCase().includes(s) ||
          (r.note ?? '').toLowerCase().includes(s) ||
          (h?.text ?? '').toLowerCase().includes(s) ||
          (r.where ?? []).some((p) => p.label.toLowerCase().includes(s)))
      );
    });
  }, [module, priority, status, hold, onlyNew, stage, q]);

  const total = REQUIREMENTS.length;
  const done = REQUIREMENTS.filter(isDone).length;
  const partial = REQUIREMENTS.filter(isPartial).length;
  const planned = total - done - partial;
  const plannedFirst = REQUIREMENTS.filter((r) => r.status === 'planned' && r.priority === 1).length;
  const customer = REQUIREMENTS.filter(waitsCustomer).length;
  const added = REQUIREMENTS.filter((r) => r.added).length;
  const selectedStage = stage === null ? null : byStage.find((s) => s.number === stage);
  const first = REQUIREMENTS.filter((r) => r.priority === 1);
  const firstDone = first.filter(isDone).length;
  const firstPartial = first.filter(isPartial).length;

  const today = todayKz();
  const lastRelease = RELEASES[0];
  const milestones = PILOT_MILESTONES.map((m) => ({ ...m, state: milestoneState(m, today) }));
  const criteria = pilotCriteria();
  const criteriaDone = criteria.filter(isDone).length;
  const ownerSteps = openOwnerSteps();

  return (
    <Page>
      <PageHeader
        title="Ход разработки"
        subtitle={`${plural(total, 'требование', 'требования', 'требований')} разделов 6–9 ТЗ (${TZ_REVISION}). По каждому видно, что сделано и где это работает в портале, что ещё нет — на каком этапе ожидается или что этому мешает.`}
        actions={
          <Link
            to="/roadmap/map"
            className="inline-flex h-11 items-center rounded-md border border-line-strong px-4 text-[14px] whitespace-nowrap text-ink hover:bg-tile/70 md:h-10"
          >
            Карта связей
          </Link>
        }
      />

      <DevBoardLink />

      <div className="visor mb-6 flex flex-col gap-1 rounded-lg border border-line bg-surface px-5 py-4">
        <p className="text-[15px] font-semibold text-ink">Состояние на {formatDay(today)}</p>
        {lastRelease ? (
          <p className="text-[14px] text-ink-2">
            Последняя выкладка {dateTime(lastRelease.at)}:{' '}
            <Link
              to="/whats-new"
              className="text-ink underline decoration-line-strong underline-offset-4 hover:text-accent-text"
            >
              {lastRelease.title}
            </Link>
          </p>
        ) : null}
        <p className="text-[14px] text-ink-2">
          Критерии приёмки: готово {criteriaDone} из {criteria.length}. Решение о старте пилота —{' '}
          {formatDay(PILOT_MILESTONES[PILOT_MILESTONES.length - 1]!.from)}.
        </p>
      </div>

      <TitleBlock
        className="mb-6"
        items={[
          {
            label: 'Готово',
            value: done,
            note: `в 1-й очереди ${firstDone} из ${first.length}; где работает — в таблице`,
            tone: 'success',
          },
          {
            label: 'Частично',
            value: partial,
            note: `в 1-й очереди ${firstPartial}; остаток — в колонке «Почему ещё нет»`,
          },
          {
            label: 'Ещё нет',
            value: planned,
            note: `в первой очереди ${plannedFirst}, во второй и третьей ${planned - plannedFirst}`,
          },
          {
            label: 'Ждёт заказчика',
            value: customer,
            note: 'решения, серверы, аккаунты и данные со стороны заказчика',
            tone: 'accent',
          },
        ]}
      />

      <section aria-label="План до пилота" className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel
          title="План до пилота"
          meta={`календарь владельца от ${formatDay(TZ_EDITION.calendar)}`}
          bodyClassName="p-0"
        >
          <ol>
            {milestones.map((m) => (
              <li
                key={m.id}
                className={clsx(
                  'flex gap-3 border-b border-line px-5 py-3 last:border-0',
                  m.state === 'past' && 'opacity-70',
                )}
              >
                <span className="num w-[72px] shrink-0 pt-0.5 text-[13px] font-semibold text-ink">
                  {span(m.from, m.to)}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-[14px] text-ink">{m.title}</span>
                  {m.note ? <span className="text-[12px] leading-[18px] text-ink-2">{m.note}</span> : null}
                </span>
                <span className="shrink-0">
                  <Badge tone={MILESTONE_TONE[m.state]}>{MILESTONE_STATE_TITLES[m.state]}</Badge>
                </span>
              </li>
            ))}
          </ol>
        </Panel>

        <Panel
          title="Ждёт владельца"
          meta={ownerSteps.length ? plural(ownerSteps.length, 'шаг', 'шага', 'шагов') : 'всё сделано'}
          bodyClassName="p-0"
        >
          {ownerSteps.length ? (
            <ul>
              {ownerSteps.map((o) => {
                const late = o.due < today;
                return (
                  <li key={o.step} className="flex gap-3 border-b border-line px-5 py-3 last:border-0">
                    <span className="num w-[72px] shrink-0 pt-0.5 text-[13px] font-semibold text-ink">
                      {o.step}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="text-[14px] text-ink">{o.title}</span>
                      <span className={clsx('text-[12px]', late ? 'text-danger' : 'text-ink-2')}>
                        срок {formatDay(o.due)}
                        {late ? ' — прошёл' : ''}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-5 py-4 text-[14px] text-ink-2">Все шаги владельца сделаны.</p>
          )}
        </Panel>

        <Panel
          className="lg:col-span-2"
          title="Критерии приёмки первой очереди"
          meta={`готово ${criteriaDone} из ${criteria.length}`}
          bodyClassName="p-0"
        >
          <ul>
            {criteria.map((r) => {
              const h = holdOf(r);
              const chats = chatsForCriterion(r.code);
              return (
                <li
                  key={r.code}
                  className="flex flex-col gap-2 border-b border-line px-5 py-3 last:border-0 md:flex-row md:gap-4"
                >
                  <span className="flex shrink-0 items-start gap-2 md:w-[150px] md:flex-col">
                    <span className="num text-[14px] font-semibold text-ink">{r.code}</span>
                    <Badge tone={STATUS_TONE[r.status]}>{REQUIREMENT_STATUS_TITLES[r.status]}</Badge>
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[14px] text-ink">{r.text.split(' Проверка:')[0]}</span>
                    {chats.length ? (
                      <span className="text-[12px] text-ink-3">
                        Ведёт: {chats.map((c) => `«${c.title}»`).join(', ')}
                      </span>
                    ) : null}
                  </span>
                  <span className="min-w-0 text-[13px] leading-[18px] text-ink-2 md:w-[34%]">
                    {h ? (
                      <>
                        <span className="font-medium text-ink">Что держит: </span>
                        {h.text}
                      </>
                    ) : r.note ? (
                      <>
                        <span className="font-medium text-ink">Что есть: </span>
                        {r.note}
                      </>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </Panel>

        <Panel
          className="lg:col-span-2"
          title="Чаты разработки"
          meta="с 1.11.2026 обе волны работают одновременно"
          bodyClassName="p-0"
        >
          <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            {DEV_CHATS.map((c) => (
              <li key={c.code} className="flex flex-col gap-0.5 border-b border-line px-5 py-3">
                <span className="text-[14px] font-medium text-ink">{c.title}</span>
                <span className="text-[12px] text-ink-2">
                  {c.wave === 0 ? c.sections : `разделы ТЗ ${c.sections}; волна ${c.wave}`}
                  {c.criteria.length ? `; ${c.criteria.join(', ')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      </section>

      {/* Все этапы видны сразу, без прокрутки вбок: сетка от двух плиток на телефоне до пяти на широком экране */}
      <section aria-label="Этапы" className="mb-6">
        <ol className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {byStage.map((s) => {
            const current = s.state === 'current';
            const selected = stage === s.number;
            return (
              <li key={s.number} className="min-w-0">
                <button
                  type="button"
                  onClick={() => setStage(selected ? null : s.number)}
                  aria-pressed={selected}
                  className={clsx(
                    'flex h-full w-full min-h-[88px] flex-col items-start gap-1 rounded-lg border px-3 py-3 text-left transition-colors md:px-4',
                    current
                      ? 'visor border-line bg-surface'
                      : 'border-line bg-surface hover:border-line-strong',
                    selected && 'border-ink',
                  )}
                >
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] font-medium text-ink-2">
                    <span
                      className={clsx(
                        'size-1.5 shrink-0 rounded-full',
                        current
                          ? 'bg-accent'
                          : s.state === 'released'
                            ? 'bg-success'
                            : s.state === 'next'
                              ? 'bg-ink'
                              : 'bg-line-strong',
                      )}
                      aria-hidden
                    />
                    Этап {s.number}
                    <span className={clsx('font-normal', current ? 'text-accent-text' : 'text-ink-3')}>
                      {s.state === 'released' && s.releasedAt
                        ? `выложен ${formatDay(s.releasedAt)}`
                        : s.state === 'current' && s.releasedAt
                          ? `идёт, часть выложена ${formatDay(s.releasedAt)}`
                          : (s.state === 'next' || s.state === 'later') && (s.done || s.partial)
                            ? 'начат'
                            : STAGE_STATE_TITLES[s.state]}
                    </span>
                  </span>
                  <span className="text-[14px] leading-5 font-semibold text-ink">{s.title}</span>
                  <span className="num text-[12px] text-ink-2">
                    {s.done || s.partial
                      ? `готово ${s.done} из ${s.total}${s.partial ? `, частично ${s.partial}` : ''}`
                      : plural(s.total, 'требование', 'требования', 'требований')}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </section>

      {selectedStage ? (
        <div className="-mt-3 mb-5 max-w-3xl text-[14px] text-ink-2">
          <p>{selectedStage.summary}</p>
          {selectedStage.needs ? (
            <p className="mt-1">
              <span className="font-medium text-ink">Заранее нужно:</span> {selectedStage.needs}.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1fr_320px]">
        <Panel title="Требования" meta={plural(rows.length, 'строка', 'строки', 'строк')} bodyClassName="p-0">
          <div className="grid gap-3 border-b border-line p-4 sm:grid-cols-2 lg:grid-cols-5">
            <Input
              placeholder="Код, текст, место, причина"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск"
            />
            <Select value={module} onChange={(e) => setModule(e.target.value)} aria-label="Модуль">
              <option value="">Все модули</option>
              {modules.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.title}
                </option>
              ))}
            </Select>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)} aria-label="Очередь">
              <option value="">Все очереди</option>
              <option value="1">1 очередь</option>
              <option value="2">2 очередь</option>
              <option value="3">3 очередь</option>
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Статус">
              <option value="">Любой статус</option>
              <option value="done_any">Сделано хоть частично</option>
              <option value="not_done">Ещё не сделано целиком</option>
              {(Object.keys(REQUIREMENT_STATUS_TITLES) as RequirementStatus[]).map((s) => (
                <option key={s} value={s}>
                  {REQUIREMENT_STATUS_TITLES[s]}
                </option>
              ))}
            </Select>
            <Select value={hold} onChange={(e) => setHold(e.target.value)} aria-label="Почему ещё нет">
              <option value="">Любая причина</option>
              {holds.map((h) => (
                <option key={h.kind} value={h.kind}>
                  {h.title} ({h.count})
                </option>
              ))}
            </Select>
            <Checkbox
              className="sm:col-span-2 lg:col-span-5"
              label={`Только добавленное в ТЗ после первой редакции реестра (${added})`}
              checked={onlyNew}
              onChange={(e) => setOnlyNew(e.target.checked)}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="block w-full text-[14px] md:table">
              <thead className="hidden bg-surface-2 text-left text-[12px] text-ink-2 md:table-header-group">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Код</th>
                  <th className="w-[34%] px-4 py-2.5 font-medium">Требование</th>
                  <th className="w-[20%] px-4 py-2.5 font-medium">Где в портале</th>
                  <th className="px-4 py-2.5 font-medium">Когда</th>
                  <th className="w-[22%] px-4 py-2.5 font-medium">Почему ещё нет</th>
                  <th className="px-4 py-2.5 font-medium">Статус</th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {rows.map((r) => (
                  <tr
                    key={r.code}
                    className="flex flex-col gap-2 border-t border-line px-4 py-3 md:table-row md:px-0 md:py-0"
                  >
                    <td className="num font-semibold text-ink md:px-4 md:py-3 md:align-top md:whitespace-nowrap">
                      {r.code}
                      <span className="ml-2 font-normal text-ink-3 md:hidden">
                        {moduleTitle(r.module)}, раздел {r.section}, {r.priority} очередь
                      </span>
                    </td>
                    <td className="text-ink md:px-4 md:py-3 md:align-top">
                      {r.text}
                      {r.note ? <div className="mt-1 text-[12px] text-ink-2">{r.note}</div> : null}
                      <div className="mt-1 hidden text-[12px] text-ink-3 md:block">
                        {moduleTitle(r.module)}, раздел {r.section}, {r.priority} очередь
                        {r.added ? ` · новое в ТЗ от ${formatDay(r.added)}` : ''}
                      </div>
                      {r.added ? (
                        <span className="mt-1 inline-block text-[12px] text-accent-text md:hidden">
                          новое в ТЗ от {formatDay(r.added)}
                        </span>
                      ) : null}
                    </td>
                    <td className="text-[13px] md:px-4 md:py-3 md:align-top">
                      <span className="mr-1 text-ink-3 md:hidden">Где:</span>
                      <Where r={r} />
                    </td>
                    <td className="text-[13px] text-ink-2 md:px-4 md:py-3 md:align-top">
                      <span className="mr-1 text-ink-3 md:hidden">Когда:</span>
                      {whenOf(r)}
                    </td>
                    <td className="md:px-4 md:py-3 md:align-top">
                      <Why r={r} />
                    </td>
                    <td className="md:px-4 md:py-3 md:align-top">
                      <Badge tone={STATUS_TONE[r.status]}>{REQUIREMENT_STATUS_TITLES[r.status]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 ? (
              <p className="px-4 py-8 text-center text-[14px] text-ink-2">Под эти условия требований нет.</p>
            ) : null}
          </div>
          <p className="border-t border-line px-4 py-3 text-[12px] leading-[18px] text-ink-3">
            Этапы 0–3 выложены, 4–13 идут одновременно по чатам, 14 — приёмка первой очереди; сроки — по
            ускоренному календарю владельца (ТЗ v2.0). Этап — место требования в плане; что выложено раньше
            своего этапа, видно по дате в колонке «Когда». Этот же реестр отдаёт API: /api/v1/roadmap.
          </p>
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel title="Почему ещё нет" bodyClassName="p-0">
            <ul>
              {holds
                .filter((h) => h.count)
                .map((h) => (
                  <li key={h.kind}>
                    <button
                      type="button"
                      onClick={() => setHold(hold === h.kind ? '' : h.kind)}
                      className={clsx(
                        'flex w-full items-center justify-between gap-3 border-b border-line px-5 py-2.5 text-left last:border-0 hover:bg-surface-2',
                        hold === h.kind && 'bg-surface-2',
                      )}
                    >
                      <span className="flex items-center gap-2 text-[14px] text-ink">
                        <Badge tone={HOLD_TONE[h.kind]}>{h.title}</Badge>
                      </span>
                      <span className="num text-[13px] text-ink-2">{h.count}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </Panel>

          <Panel title="По модулям" bodyClassName="p-0">
            <ul>
              {modules.map((m) => {
                const pct = m.total ? Math.round((m.done / m.total) * 100) : 0;
                const pctPartial = m.total ? Math.round((m.partial / m.total) * 100) : 0;
                return (
                  <li key={m.code}>
                    <button
                      type="button"
                      onClick={() => setModule(module === m.code ? '' : m.code)}
                      className={clsx(
                        'flex w-full flex-col gap-1.5 border-b border-line px-5 py-3 text-left last:border-0 hover:bg-surface-2',
                        module === m.code && 'bg-surface-2',
                      )}
                    >
                      <span className="flex w-full items-baseline justify-between gap-2">
                        <span className="text-[14px] font-medium text-ink">{m.title}</span>
                        <span className="num text-[12px] text-ink-2">
                          {m.done} / {m.total}
                        </span>
                      </span>
                      <span className="flex h-1.5 w-full overflow-hidden rounded-full bg-tile" aria-hidden>
                        <span className="h-full bg-success" style={{ width: `${pct}%` }} />
                        <span className="h-full bg-warning/60" style={{ width: `${pctPartial}%` }} />
                      </span>
                      <span className="text-[12px] text-ink-2">
                        в 1 очереди {m.first}
                        {m.partial ? `, частично ${m.partial}` : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Panel>
        </div>
      </div>
    </Page>
  );
}
